import React, { useState, useRef } from "react";
import { IoClose } from "react-icons/io5";
import { LuCheck } from "react-icons/lu";
import Modal from "react-responsive-modal";
import { useTaskContext } from "../../../context/TaskContext";
import { useSelector } from "react-redux";
import { useMutation } from "@apollo/client";
import { CREATE_DUPLICATE_TASK } from "../../../apollo/Mutations";
import toast from "react-hot-toast";

const TaskDuplicatePopup = ({ isOpen, onClose }) => {
  const { user } = useTaskContext();
  const { active_task } = useSelector(state => state.task);



  // Editing
  const [loader, setLoader] = useState(false);
  const [editing, setEditing] = useState(false);
  const [titleInput, setTitleInput] = useState(active_task.task_title);
  const inputRef = useRef(null);

  // Checkbox states
  const [options, setOptions] = useState({
    includeFiles: false,
    retainDates: false,
    keepOwner: false,
    keepAssignedUsers: false,
    retainObservers: false,
  });

  const toggleOption = (key) => {
    setOptions({ ...options, [key]: !options[key] });
  };

  const closePopup = () => onClose();

  const saveTitle = () => {
    if (titleInput.trim() === "") return;
    // setActiveTask({ ...active_task, task_title: titleInput });
    setEditing(false);
  };

  const cancelEdit = () => {
    setTitleInput(active_task.task_title);
    setEditing(false);
  };

  const [create_duplicate] = useMutation(CREATE_DUPLICATE_TASK)

  const duplicateTask = async () => {
    const toastId = toast.loading('Duplicating task...');
    setLoader(true);
    try {
      const { data } = await create_duplicate({
        variables: {
          input: {
            task_id: active_task?._id,
            task_title: titleInput === active_task?.task_title ? null : titleInput,
            start_date: options.retainDates ? 'yes' : 'no',
            due_date: options.retainDates ? 'yes' : 'no',
            due_time: options.retainDates ? 'yes' : 'no',
            owner: options.keepOwner ? 'yes' : 'no',
            assigned_to: options.keepAssignedUsers ? 'yes' : 'no',
            observers: options.retainObservers ? 'yes' : 'no',
          }
        }
      })
      if (data) {
        toast.success('Task duplicated successfully!', { id: toastId });
      } else {
        toast.error('Failed to duplicate task', { id: toastId });
      }
      setLoader(false);
      onClose()
    } catch (error) {
      setLoader(false);
      console.log(error);
      toast.error('Failed to duplicate task', { id: toastId });
    }
  }

  const closeIcon = (
    <div className="privateCloseIcon text-3xl mt-1 text-teal-600 dark:text-white" />
  );

  return (
    <Modal
      open={isOpen}
      onClose={closePopup}
      center
      closeIcon={closeIcon}
      focusTrapped={false}
      classNames={{
        overlay: "customOverlay",
        modal: "customModal bgSurfaceDarkMainBox dark:border dark:border-[#d6d6d635]",
      }}
    >
      <div>
        <h2 className="text-lg text-teal-600 dark:text-white font-semibold">
          Task Duplicate
        </h2>

        {/* Task Title */}
        <div className="flex gap-1 items-center w-full mt-8">
          {editing ? (
            <div className="flex gap-2 items-center w-full">
              <input
                ref={inputRef}
                className="border-b w-[90%] rounded pl-1 pr-1 py-0.5 break-all border border-teal-600 text-teal-600 text-sm outline-none"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
              />

              <div className="flex gap-2">
                <IoClose
                  className="text-lg text-text-body cursor-pointer"
                  onClick={cancelEdit}
                />
                <LuCheck
                  className={`text-base text-text-body cursor-pointer ${active_task.task_title === titleInput.trim()
                    ? "opacity-50 pointer-events-none"
                    : ""
                    }`}
                  onClick={saveTitle}
                />
              </div>
            </div>
          ) : (
            <div className="flex gap-1 items-center w-full">
              <p className="text-teal-600 text-base font-bold text-ellipsis whitespace-nowrap overflow-hidden w-full">
                {active_task.task_title}
              </p>

              <div
                onClick={() => {
                  setEditing(true);
                  setTitleInput(active_task.task_title);
                }}
                className="editPenIcons w-4 h-4 cursor-pointer"
                style={{ backgroundSize: "16px" }}
              />
            </div>
          )}
        </div>

        {/* Checkbox List */}
        <div className="mt-6 space-y-3">
          {/* Include Attached Files */}
          {/* <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4"
              checked={options.includeFiles}
              onChange={() => toggleOption("includeFiles")}
            />
            <span className="text-teal-600 dark:text-white">
              Include attached files
            </span>
          </label> */}

          {/* Retain Original Dates */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4"
              checked={options.retainDates}
              onChange={() => toggleOption("retainDates")}
            />
            <span className="text-teal-600 dark:text-white">
              Retain original dates
            </span>
          </label>

          {/* Keep Original Owner */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4"
              checked={options.keepOwner}
              onChange={() => toggleOption("keepOwner")}
            />
            <span className="text-teal-600 dark:text-white">
              Keep original owner
            </span>
          </label>

          {/* Keep Assigned Users */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4"
              checked={options.keepAssignedUsers}
              onChange={() => toggleOption("keepAssignedUsers")}
            />
            <span className="text-teal-600 dark:text-white">
              Keep assigned users
            </span>
          </label>

          {/* Retain Observers */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4"
              checked={options.retainObservers}
              onChange={() => toggleOption("retainObservers")}
            />
            <span className="text-teal-600 dark:text-white">
              Retain observers
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2.5 mt-8">
          <button
            onClick={closePopup}
            className={`px-4 py-2 border border-teal-200 text-teal-600 rounded-md dark:border dark:text-white ${loader ? 'opacity-50 pointer-events-none' : ''}`}
          >
            Cancel
          </button>
          <button className={`px-4 py-2 bg-teal-600 text-white rounded-md min-w-[97px] ${loader ? "pointer-events-none btn_loader" : ""}`} onClick={duplicateTask}>
            {loader ? '' : 'Confirm'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TaskDuplicatePopup;
